// Browser-side reader for the narrow normal-chat SSE endpoint. Once this
// explicit client opt-in is enabled, a normal turn must remain on the GPT lane:
// a non-SSE response is surfaced as a technical error, never sent to the old
// local/canned conversation path.

export function normalChatGptStreamEnabled() {
  return import.meta.env.VITE_ARIA_NORMAL_CHATGPT_ENABLED === '1';
}

function parseFrame(frame) {
  const lines = frame.replace(/\r/g, '').split('\n');
  let type = 'message';
  const data = [];
  for (const line of lines) {
    if (line.startsWith('event:')) type = line.slice(6).trim();
    if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
  }
  if (!data.length) return null;
  try { return { type, data: JSON.parse(data.join('\n')) }; } catch (_) { return null; }
}

export const NORMAL_CHAT_STREAM_TIMEOUT_MS = 35000;

// This route spends GPT budget. Signed-in account sessions keep using the
// normal account token. A first-time browser may obtain a separate, narrow
// guest token that is accepted only by this route; it is never stored in the
// account token key or attached by the global authenticated fetch wrapper.
const ACCOUNT_TOKEN_KEY = 'aria_session_token';
const GUEST_TOKEN_KEY = 'aria_guest_normal_chat_token';
const GUEST_EXPIRY_KEY = 'aria_guest_normal_chat_expires_at';

function readStorage(key) {
  try { return typeof localStorage !== 'undefined' ? String(localStorage.getItem(key) || '').trim() : ''; }
  catch (_) { return ''; }
}

function clearGuestSession() {
  try {
    localStorage.removeItem(GUEST_TOKEN_KEY);
    localStorage.removeItem(GUEST_EXPIRY_KEY);
  } catch (_) {}
}

function guestToken() {
  const token = readStorage(GUEST_TOKEN_KEY);
  const expiry = Date.parse(readStorage(GUEST_EXPIRY_KEY));
  if (!token || !Number.isFinite(expiry) || expiry <= Date.now()) {
    if (token) clearGuestSession();
    return '';
  }
  return token;
}

function sessionAuthHeaders() {
  const token = readStorage(ACCOUNT_TOKEN_KEY) || guestToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

let guestSessionInFlight = null;

function guestSessionUrl(url) {
  return new URL('/api/auth/guest-session', url || (typeof window !== 'undefined' ? window.location.href : 'http://localhost')).toString();
}

async function ensureNormalChatSession(url) {
  // The transport is imported by server-side/unit tooling too. Guest bootstrap
  // is a browser-only onboarding path and must not make those callers depend
  // on browser storage or an absolute browser URL.
  if (typeof localStorage === 'undefined') return;
  if (sessionAuthHeaders().Authorization) return;
  if (guestSessionInFlight) return guestSessionInFlight;
  guestSessionInFlight = (async () => {
    const response = await fetch(guestSessionUrl(url), {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload?.token || !payload?.expiresAt) {
      const error = new Error('Could not establish the limited guest chat session.');
      error.code = payload?.error || 'GUEST_SESSION_UNAVAILABLE';
      throw error;
    }
    const expiry = Date.parse(payload.expiresAt);
    if (!Number.isFinite(expiry) || expiry <= Date.now()) {
      const error = new Error('Guest chat session was invalid.');
      error.code = 'GUEST_SESSION_INVALID';
      throw error;
    }
    try {
      localStorage.setItem(GUEST_TOKEN_KEY, String(payload.token));
      localStorage.setItem(GUEST_EXPIRY_KEY, payload.expiresAt);
    } catch (_) {
      const error = new Error('Guest chat session could not be stored.');
      error.code = 'GUEST_SESSION_STORAGE_UNAVAILABLE';
      throw error;
    }
  })();
  try {
    return await guestSessionInFlight;
  } finally {
    guestSessionInFlight = null;
  }
}

// THE LATENCY WATERFALL (9/26): the browser's own wall-clock stamp for one voice
// turn, posted to the backend's store; never inferred from arrival time.
export function stampTurn(url, turnId, stage, meta) {
  try {
    if (!turnId || typeof fetch !== 'function') return;
    const stampUrl = new URL('/api/pipeline/stamp', url || (typeof window !== 'undefined' ? window.location.href : 'http://localhost')).toString();
    Promise.resolve(fetch(stampUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ turnId, stage, at: Date.now(), meta: meta || undefined }) })).catch(() => {});
  } catch (_) {}
}

function timeoutError() {
  const error = new Error('Normal GPT chat timed out before it completed.');
  error.name = 'TimeoutError';
  error.code = 'NORMAL_CHAT_GPT_CLIENT_TIMEOUT';
  return error;
}

// Normal voice failures used to leave the pipeline at `voice.sent`: this
// transport has the next observable boundary.  The records intentionally
// contain only state/status, never a transcript, token, or response text.
function reportNormalChatStage(url, stage, meta = {}) {
  if (typeof fetch !== 'function') return;
  try {
    const eventUrl = new URL('/api/pipeline/event', url || (typeof window !== 'undefined' ? window.location.href : 'http://localhost')).toString();
    Promise.resolve(fetch(eventUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage, level: 'ok', text: 'normal-gpt', meta, reqId: meta?.turnId || '' }),
    })).catch(() => {});
  } catch (_) {}
}

export async function streamNormalChat({ url, body, turnId, turnStartedAt, onDelta, onPhrase, onRetry, onDone, onWorkspace, onStage, signal, timeoutMs = NORMAL_CHAT_STREAM_TIMEOUT_MS }) {
  const controller = new AbortController();
  let timedOut = false;
  const deadline = Math.max(1000, Number(timeoutMs) || NORMAL_CHAT_STREAM_TIMEOUT_MS);
  const now = () => typeof performance !== 'undefined' ? performance.now() : Date.now();
  const startedAt = now();
  const emitStage = (stage, meta = {}) => {
    const at = now();
    const payload = {
      ...meta,
      elapsedMs: Math.max(0, Math.round(at - (Number.isFinite(turnStartedAt) ? turnStartedAt : startedAt))),
      stageMs: Math.max(0, Math.round(at - startedAt)),
      ...(turnId ? { turnId } : {}),
    };
    reportNormalChatStage(url, stage, payload);
    try { onStage?.(stage, payload); } catch (_) { /* diagnostics must not affect replies */ }
  };
  const timer = setTimeout(() => {
    timedOut = true;
    emitStage('normal.gpt.timeout');
    controller.abort();
  }, deadline);
  const abortFromCaller = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', abortFromCaller, { once: true });
  }

  try {
    await ensureNormalChatSession(url);
    const authHeaders = sessionAuthHeaders();
    emitStage('normal.gpt.request', { hasSession: !!authHeaders.Authorization });
    // the waterfall's request.sent: stamped here, after the guest-session fetch, right before the request leaves
    const _vtId = body && body.clientTiming && body.clientTiming.turnId ? String(body.clientTiming.turnId) : '';
    const _body = { ...body, normalChatGptStream: true };
    if (_vtId) _body.clientTiming = { ...body.clientTiming, sentAt: Date.now() };
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...authHeaders,
      },
      body: JSON.stringify(_body),
      signal: controller.signal,
    });
    const contentType = String(response.headers.get('content-type') || '').toLowerCase();
    emitStage('normal.gpt.headers', { status: response.status || 0, stream: contentType.includes('text/event-stream') });
    if (!response.ok || !contentType.includes('text/event-stream') || !response.body) {
      emitStage('normal.gpt.rejected', { status: response.status || 0, stream: contentType.includes('text/event-stream') });
      return {
        handled: true,
        status: response.status,
        reply: '',
        error: { code: `NORMAL_CHAT_GPT_HTTP_${response.status || 0}` },
      };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let pending = '';
    let reply = '';
    let done = null;
    let latestWorkspace = null;
    let streamError = null;
    let sawFirstDelta = false;

    const consume = async (frame) => {
      const event = parseFrame(frame);
      if (!event) return;
      if (event.type === 'delta' && typeof event.data?.text === 'string') {
        if (!sawFirstDelta) {
          sawFirstDelta = true;
          emitStage('normal.gpt.first_delta');
          if (_vtId) stampTurn(url, _vtId, 'reply.received', { path: 'stream-delta' });
        }
        reply += event.data.text;
        if (typeof onDelta === 'function') {
          // A renderer callback must never abort the network stream. If a UI
          // update throws, keep consuming the model response so the final
          // reply can still be rendered and spoken.
          try { await onDelta(event.data.text, reply); }
          catch (error) { emitStage('normal.gpt.client_callback_error', { callback: 'delta', errorName: error?.name || 'Error' }); }
        }
      } else if (event.type === 'phrase' && typeof event.data?.text === 'string') {
        if (typeof onPhrase === 'function') {
          try { await onPhrase(event.data.text); }
          catch (error) { emitStage('normal.gpt.client_callback_error', { callback: 'phrase', errorName: error?.name || 'Error' }); }
        }
      } else if (event.type === 'workspace') {
        latestWorkspace = event.data?.taskWorkspace || latestWorkspace;
        if (typeof onWorkspace === 'function') {
          try { await onWorkspace(latestWorkspace); }
          catch (error) { emitStage('normal.gpt.client_callback_error', { callback: 'workspace', errorName: error?.name || 'Error' }); }
        }
      } else if (event.type === 'retry') {
        reply = '';
        latestWorkspace = null;
        if (typeof onRetry === 'function') await onRetry(event.data || {});
      } else if (event.type === 'done') {
        done = event.data || {};
        emitStage('normal.gpt.done', { hasReply: !!String(done.reply || reply || '').trim() });
        if (typeof onDone === 'function') await onDone(done);
      } else if (event.type === 'error') {
        streamError = event.data || { code: 'NORMAL_CHAT_GPT_STREAM_FAILED' };
        emitStage('normal.gpt.error', { code: streamError.code || 'NORMAL_CHAT_GPT_STREAM_FAILED' });
      }
    };

    for (;;) {
      const { value, done: readerDone } = await reader.read();
      pending += decoder.decode(value || new Uint8Array(), { stream: !readerDone });
      let boundary;
      while ((boundary = pending.indexOf('\n\n')) >= 0) {
        const frame = pending.slice(0, boundary);
        pending = pending.slice(boundary + 2);
        await consume(frame);
      }
      if (readerDone) break;
    }
    if (pending.trim()) await consume(pending);
    return {
      handled: true,
      reply: String(done?.reply || reply || '').trim(),
      source: done?.source || null,
      reasoningEffort: done?.reasoningEffort || null,
      assistOffer: done?.assistOffer || null,
      // First-time onboarding (web only, 9/26): the server's gate answers a new
      // visitor's first turn here and marks it; App.jsx routes the rest of the
      // flow to /api/ai/conversation off this signal (see onboardingRoute.js).
      onboarding: done?.onboarding || null,
      taskWorkspace: done?.taskWorkspace || latestWorkspace,
      error: streamError,
    };
  } catch (error) {
    if (timedOut) {
      emitStage('normal.gpt.timeout');
      throw timeoutError();
    }
    emitStage('normal.gpt.transport_error', { name: error?.name || 'Error' });
    throw error;
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', abortFromCaller);
  }
}
